import type { JSONContent } from '@tiptap/react';
import { z } from 'zod';

import { type Asset, imageAssetId } from './media';
import { mutationSchema, type Note, type RecordData, validateValue } from './model';
import type { Change } from './productivity';
import { validateProductivity } from './productivity';

export async function imageManifest(records: Pick<RecordData, 'id' | 'kind' | 'value'>[]) {
  const images: {
    noteId: string;
    path: number[];
    sha256: string;
    bytes: number;
    mediaType: string;
  }[] = [];

  async function visit(node: JSONContent, noteId: string, path: number[]) {
    if (node.type === 'image') {
      const id = imageAssetId(node),
        asset = id
          ? (records.find(r => r.kind === 'asset' && r.id === id)?.value as Asset | undefined)
          : undefined;

      if (id && !asset) {
        throw new Error('An image has not finished downloading.');
      }

      const src = asset?.data ?? String(node.attrs?.src ?? ''),
        match = /^data:(image\/[a-z]+);base64,(.+)$/.exec(src);

      if (!match) {
        throw new Error('An image is not embedded. Add it as a local image before exporting.');
      }

      const bytes = Uint8Array.from(atob(match[2]), c => c.charCodeAt(0));
      const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));

      images.push({
        noteId,
        path,
        sha256: Array.from(hash, b => b.toString(16).padStart(2, '0')).join(''),
        bytes: bytes.length,
        mediaType: match[1],
      });
    }

    for (let i = 0; i < (node.content?.length ?? 0); i++) {
      await visit(node.content![i], noteId, [...path, i]);
    }
  }

  for (const record of records) {
    if (record.kind === 'note') {
      await visit((record.value as Note).content, record.id, []);
    }
  }

  return images;
}

export async function exportWorkspace(
  records: (Pick<RecordData, 'id' | 'kind' | 'value'> & { deleted?: boolean })[],
) {
  const ordinary = records
    .filter(r => r.kind !== 'vault')
    .map(r => ({
      id: r.id,
      kind: r.kind,
      value: validateValue(r.kind, r.value),
      ...(r.deleted ? { deleted: true } : {}),
    }));

  return {
    format: 'cairn-workspace',
    version: 1,
    createdAt: new Date().toISOString(),
    records: ordinary,
    images: await imageManifest(ordinary),
  };
}

const exportSchema = z.object({
  format: z.literal('cairn-workspace'),
  version: z.literal(1),
  createdAt: z.string().datetime(),
  records: z
    .array(mutationSchema.pick({ id: true, kind: true, value: true, deleted: true }))
    .max(50000),
  images: z
    .array(
      z.object({
        noteId: z.string().uuid(),
        path: z.array(z.number().int().nonnegative()),
        sha256: z.string().regex(/^[a-f0-9]{64}$/),
        bytes: z.number().int().nonnegative(),
        mediaType: z.string(),
      }),
    )
    .max(30000),
});

export async function importWorkspace(input: unknown): Promise<Change[]> {
  const data = exportSchema.parse(input);

  if (data.records.some(r => r.kind === 'vault')) {
    throw new Error('Vault records require their protected export format.');
  }

  if (new Set(data.records.map(r => r.id)).size !== data.records.length) {
    throw new Error('Duplicate record IDs');
  }

  const records = data.records.map(r => ({ ...r, value: validateValue(r.kind, r.value) }));
  const manifest = await imageManifest(records);

  if (JSON.stringify(manifest) !== JSON.stringify(data.images)) {
    throw new Error('Image manifest verification failed');
  }

  validateProductivity(records);

  return records;
}
